import { useEffect, useState } from 'react';
import { hasLocalVault, serverStatus } from './auth.ts';
import { useApp } from './store.ts';
import { store } from '../data/db.ts';
import { Lock } from '../ui/Lock.tsx';
import { applyUpdate, onUpdateAvailable } from './updates.ts';
import { NoteList } from '../ui/NoteList.tsx';
import { NoteView } from '../ui/NoteView.tsx';
import { DocumentView } from '../ui/DocumentView.tsx';
import { Settings } from '../ui/Settings.tsx';
import { Todos } from '../ui/Todos.tsx';
import { Archive } from '../ui/Archive.tsx';
import { Funde } from '../ui/Funde.tsx';
import { Palette } from '../ui/Palette.tsx';
import { Shared } from '../ui/Shared.tsx';
import { EmbedSuche, isEmbed } from '../ui/EmbedSuche.tsx';
import { startPushSync } from '../todos/push.ts';

export function App(): JSX.Element {
  // Geteilte Notizen sind eine eigene, kontolose Ansicht.
  if (location.pathname.startsWith('/s/')) return <Shared />;

  // Die eingebettete Suche teilt sich Start, Sperre und Entsperren mit der App und
  // weicht erst beim Rendern ab — deshalb kein eigener Zweig wie bei `/s/`.
  const embed = isEmbed();

  const {
    phase,
    setPhase,
    enter,
    notes,
    documents,
    funde,
    activeId,
    lockAfter,
    leave,
    showTodos,
    setShowTodos,
    showArchive,
    setShowArchive,
    showFunde,
    setShowFunde,
  } = useApp();
  const [email, setEmail] = useState<string | null>(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => onUpdateAvailable(setUpdateReady), []);
  const [needsSetupToken, setNeedsSetupToken] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPalette, setShowPalette] = useState(false);

  // Cmd/Strg+K ist die einzige Tastenkombination, die man kennen muss.
  useEffect(() => {
    // Im Rahmen gibt es weder Palette noch Editor — ⌘N legte dort unsichtbar Notizen an.
    if (embed) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setShowPalette((open) => !open);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        void useApp.getState().create();
      }
      if (event.altKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        useApp.getState().toggleDensity();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embed]);

  useEffect(() => {
    void (async () => {
      const local = await hasLocalVault();
      if (local) {
        setEmail((await store.getAuth())?.email ?? null);
        setPhase('locked');
        return;
      }
      const status = await serverStatus();
      const initialized = status?.initialized ?? null;
      setNeedsSetupToken(status?.needsSetupToken === true);
      // Offline und ohne lokalen Vault lässt sich nichts entscheiden — dann fragen wir
      // nach der Passphrase und der Nutzer merkt es beim Versuch.
      setPhase(initialized === false ? 'setup' : 'locked');
    })();
  }, [setPhase]);

  /**
   * Die vorgemerkten Zeitpunkte für Mitteilungen liegen beim Server und veralten mit
   * jeder Änderung. Sie lassen sich nur bei entsperrtem Vault neu berechnen — die
   * Erinnerungen sind sonst nicht lesbar.
   */
  useEffect(() => {
    // Der Rahmen in Zeus ist kein Gerät, das Mitteilungen bekommen soll.
    if (phase !== 'ready' || embed) return undefined;
    return startPushSync();
  }, [phase, embed]);

  /**
   * Eine Notiz lässt sich über `/#note=<id>` öffnen — der Weg, den ein Treffer aus
   * der eingebetteten Suche nimmt. Hinter dem `#`, wie beim Teilen: Was dort steht,
   * sendet der Browser nie an den Server. Die Kennung wird nach dem Öffnen aus der
   * Adresse genommen, damit ein Neuladen nicht wieder dieselbe Notiz aufreißt.
   */
  useEffect(() => {
    if (phase !== 'ready' || embed) return;
    const id = new URLSearchParams(location.hash.replace(/^#/, '')).get('note');
    if (!id) return;
    history.replaceState(null, '', location.pathname + location.search);
    const state = useApp.getState();
    const known = [...state.notes, ...state.documents, ...state.funde].some((note) => note.id === id);
    if (known) state.open(id);
  }, [phase, embed]);

  // Auto-Sperre: nach Untätigkeit vergisst die App den Schlüssel im Arbeitsspeicher.
  useEffect(() => {
    if (phase !== 'ready' || lockAfter <= 0) return undefined;
    let timer = 0;
    const reset = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => leave(), lockAfter * 60_000);
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'visibilitychange'] as const;
    for (const name of events) window.addEventListener(name, reset, { passive: true });
    reset();
    return () => {
      window.clearTimeout(timer);
      for (const name of events) window.removeEventListener(name, reset);
    };
  }, [phase, lockAfter, leave]);

  /**
   * Steht über allem, auch über dem Sperrbildschirm: Eine neue Fassung kommt meist an,
   * während die App im Hintergrund lag — und dann ist sie beim Zurückkommen gesperrt.
   * Dort nicht zu erscheinen hieße, den Hinweis fast immer zu verpassen.
   */
  const updateBar = updateReady ? (
    <div className="update-bar" role="status">
      <span>
        Neue Fassung verfügbar.
        <em>Beim Aktualisieren wird der Vault gesperrt.</em>
      </span>
      <button
        className="solid"
        disabled={updating}
        onClick={() => {
          setUpdating(true);
          void applyUpdate();
        }}
      >
        {updating ? 'Wird geladen …' : 'Aktualisieren'}
      </button>
      <button className="update-later" onClick={() => setUpdateReady(false)} aria-label="Später">
        ×
      </button>
    </div>
  ) : null;

  if (phase === 'loading') return <div className="boot">Athene</div>;

  if (phase === 'setup' || phase === 'locked') {
    const lock = (
      <Lock
        mode={phase}
        email={email}
        needsSetupToken={needsSetupToken}
        onUnlocked={() => void enter()}
      />
    );
    // Derselbe Dialog im Rahmen — der Schlüssel entsteht dort, wo die Suche läuft,
    // und das Elternfenster bekommt ihn nie zu sehen. Die Komponente bleibt über das
    // Entsperren hinweg dieselbe, damit ein schon wartender Suchbegriff nicht verfällt.
    if (embed) return <EmbedSuche phase={phase} lock={lock} />;
    return (
      <>
        {updateBar}
        {lock}
      </>
    );
  }

  if (embed) return <EmbedSuche phase="ready" />;

  // Ein Dokument ist technisch eine Notiz und wird im selben Bereich geöffnet — es
  // steht nur nicht in der Notizliste.
  const active =
    notes.find((note) => note.id === activeId) ??
    documents.find((entry) => entry.id === activeId) ??
    funde.find((entry) => entry.id === activeId) ??
    null;

  return (
    <div className={`app${active || showTodos ? ' app-open' : ''}`}>
      {updateBar}
      <NoteList onOpenPalette={() => setShowPalette(true)} />
      {showTodos ? (
        <Todos onClose={() => setShowTodos(false)} />
      ) : showArchive ? (
        <Archive onClose={() => setShowArchive(false)} />
      ) : showFunde ? (
        <Funde onClose={() => setShowFunde(false)} />
      ) : active?.dokument ? (
        // Ein Dokument ist keine Notiz und wird auch nicht wie eine geöffnet.
        <DocumentView note={active as typeof active & { dokument: NonNullable<typeof active.dokument> }} />
      ) : active ? (
        <NoteView note={active} onOpenPalette={() => setShowPalette(true)} />
      ) : (
        <section className="note note-empty">
          <p>Noch keine Notiz geöffnet.</p>
          <div>
            <span>
              <span className="key">⌘N</span> neue Notiz
            </span>
            <span>
              <span className="key">⌘K</span> alles andere
            </span>
          </div>
        </section>
      )}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}
      {showPalette && (
        <Palette
          onClose={() => setShowPalette(false)}
          onOpenSettings={() => setShowSettings(true)}
        />
      )}
    </div>
  );
}
